import React from 'react';
import { formatCurrency } from '../../utils/formatCurrency';
import { moneyOrAbsent } from '../../utils/moneyOrAbsent';
import { formatDate } from '../../utils/dateFormat';
import { BTN } from '../../constants/buttons';
import { MODAL_OVERLAY, MODAL_PANEL_MOBILE_CAP } from '../../constants/overlayLayers';

/**
 * The keep-or-remove dialog `patchHireWithAdjustmentDecision`
 * (`utils/hirePatchWithAdjustmentDecision.js`) opens when a recompute
 * (extend / shorten / change-start) 409s with the server's
 * `adjustments_exist` shape — the item already carries an attributed hire
 * price adjustment and the date change would otherwise silently keep or
 * silently delete it.
 *
 * ⚖️ Pete, 2026-09-01: "go with B, but give the person the option to keep
 * the overide or remove it." 🔑 **The attribution is the point** — the same
 * day's ruling, "every skip or overide etc should have a by who in the
 * audit" — so this shows the PERSON who made the adjustment, not just its
 * amount: `adjustment_by_name` and `adjustment_created_at` render beside
 * every row, never just the money.
 *
 * Modal chrome deliberately mirrors `HireActionModal.js` /
 * `HireMoneyWarningDialog.js`'s fixed-inset shell rather than wrapping the
 * shared `ConfirmDialog` — the three fixed testids this dialog's callers
 * need (`adjustment-keep` / `adjustment-remove` / `adjustment-cancel`) do
 * not fit `ConfirmDialog`'s hard-coded button set (its confirm button
 * carries no testid at all, and its optional third button is pinned to
 * `confirm-dialog-alt`). The BUTTON CLASSES are still copied from the shared
 * source rather than invented — `BTN.confirm` for Keep, `BTN.danger` for
 * Remove (the same class `ConfirmDialog`'s own `variant="danger"` confirm
 * button uses), `BTN.secondary` for Cancel.
 * 🔻 Keep was `BTN.primary` (blue) until 2026-09-25 and this docblock
 * recorded that as a deliberate choice. Owner ruling that day, put to him
 * for this button individually: Keep commits the adjustment, and a
 * save/commit is the green `BTN.confirm`; blue is for start/create/navigate.
 *
 * `adjustments` — the server's `resolveAdjustmentDecision` row shape,
 * `{ id, line_total, adjustment_note, adjustment_created_at,
 * adjustment_by_name }[]` (usually one row, never assumed to be).
 * `onDecide('keep' | 'remove' | null)` — `null` is Cancel; the caller (the
 * wrapper's `onNeedsDecision`) resolves its own promise with whatever this
 * reports, verbatim.
 *
 * 🪤 `line_total` on an adjustment row is a REAL SIGNED NUMBER and can be
 * NEGATIVE (a credit/discount adjustment) — summed with `Number.isFinite`,
 * never `||`/`Number` coercion alone, or a genuine −£75 adjustment reads as
 * "no adjustment" the moment anyone reaches for a falsy shortcut.
 *
 * 🚨 **THE ROW'S OWN AMOUNT IS GUARDED AND THE SUM'S `Number.isFinite` IS WHY.**
 * ⚖️ Owner ruling 2026-09-21: *"Guard the absence marker AT THE CALL SITES.
 * Leave `formatCurrency` a dumb converter."* A row whose `line_total` is not a
 * finite number contributes **0** to `total` by the reducer above — so before
 * this guard the same row ALSO rendered `£0.00`, which is a real, actionable
 * figure standing in for one nobody entered. The two now agree in meaning: the
 * sum ignores it, the row says so. 🔑 **An absence is not a zero, and a real,
 * deliberate `£0.00` adjustment still renders `£0.00`.** The three states, and
 * why absent and malformed deliberately do NOT share a marker, are in
 * `utils/moneyOrAbsent.js` — read it before changing the shape here.
 */
/**
 * THE keep/remove wording, exported so the Off hire window's "Use actual
 * dates" decision (`OffHirePriceChoice.js`, owner ruling 2026-10-06, 6B) asks
 * in the SAME words — never a second phrasing of one decision.
 */
export const ADJUSTMENT_EXISTS_TITLE = 'This item already has a price adjustment';
export const ADJUSTMENT_KEEP_LABEL = 'Keep';
export const ADJUSTMENT_REMOVE_LABEL = 'Remove';
/** `total` is the signed sum of the adjustment rows; `fmt` the money formatter. */
export function adjustmentRemoveCopy(total, fmt = formatCurrency) {
  return `Remove the ${fmt(Math.abs(total))} adjustment — the order total changes by ${formatSignedMoney(-total, fmt)}.`;
}

function HireAdjustmentDecisionDialog({ adjustments, onDecide, busy = false }) {
  const rows = Array.isArray(adjustments) ? adjustments : [];
  const total = rows.reduce(
    (sum, row) => sum + (Number.isFinite(row.line_total) ? row.line_total : 0),
    0
  );
  return (
    <div className={`fixed inset-0 z-50 flex items-center justify-center p-0 md:p-4 bg-black/50 ${MODAL_OVERLAY}`} data-testid="hire-adjustment-decision-dialog">
      <div className={`bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full mx-0 md:mx-4 max-h-[90vh] overflow-y-auto ${MODAL_PANEL_MOBILE_CAP}`}>
        {/* Header */}
        <div className="px-4 md:px-5 py-4 border-b border-gray-200 dark:border-gray-700 bg-amber-50 dark:bg-amber-900/20">
          <h3 className="text-lg font-semibold text-amber-800 dark:text-amber-300">
            {ADJUSTMENT_EXISTS_TITLE}
          </h3>
          <p className="text-sm mt-0.5 text-gray-600 dark:text-gray-400">
            Changing the hire dates recomputes the price. Decide what happens to the existing adjustment before it proceeds.
          </p>
        </div>

        {/* The adjustment(s), attributed. */}
        <div className="px-4 md:px-5 py-3 border-b border-gray-100 dark:border-gray-700 space-y-2">
          {rows.map((row) => (
            <div
              key={row.id}
              className="text-sm border rounded-lg p-2 border-gray-200 dark:border-gray-700"
              data-testid={`adjustment-row-${row.id}`}
            >
              <div className="flex justify-between items-baseline gap-2">
                <span className="font-medium text-gray-900 dark:text-gray-100">
                  {moneyOrAbsent(row.line_total)}
                </span>
                <span className="text-xs text-gray-500 dark:text-gray-400 text-right">
                  {row.adjustment_by_name || 'Unknown user'} · {formatDate(row.adjustment_created_at)}
                </span>
              </div>
              {row.adjustment_note && (
                <p className="text-xs mt-1 text-gray-600 dark:text-gray-400">{row.adjustment_note}</p>
              )}
            </div>
          ))}
        </div>

        {/* What Remove does to the money. */}
        <div className="px-4 md:px-5 pt-3">
          <p className="text-xs text-gray-500 dark:text-gray-400" data-testid="adjustment-remove-copy">
            {/* Removing subtracts the rows' own signed value: the delta is its NEGATION. */}
            {adjustmentRemoveCopy(total)}
          </p>
        </div>

        {/* Buttons */}
        <div className="px-4 md:px-5 py-4 flex gap-3">
          <button
            type="button"
            onClick={() => onDecide(null)}
            disabled={busy}
            className={`flex-1 ${BTN.secondary}`}
            data-testid="adjustment-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onDecide('keep')}
            disabled={busy}
            className={`flex-1 ${BTN.confirm}`}
            data-testid="adjustment-keep"
          >
            {ADJUSTMENT_KEEP_LABEL}
          </button>
          <button
            type="button"
            onClick={() => onDecide('remove')}
            disabled={busy}
            className={`flex-1 ${BTN.danger}`}
            data-testid="adjustment-remove"
          >
            {ADJUSTMENT_REMOVE_LABEL}
          </button>
        </div>
      </div>
    </div>
  );
}

// +£X for a positive delta, −£X for a negative one, £0.00 for exactly zero.
//
// 🔻 **CORRECTED 2026-09-22.** This said *"never a bare negative number from
// formatCurrency's own `£-75.00` shape"*, and that shape NO LONGER EXISTS —
// the owner ruled the sign belongs outside the symbol, so `formatCurrency(-75)`
// is now `-£75.00`. The old justification is therefore dead.
//
// 🔑 **THIS FUNCTION STILL EARNS ITS PLACE, FOR THE OTHER REASON: it renders a
// SIGNED DELTA, not an amount.** It has a `+` case that `formatCurrency` has no
// use for, and it uses a U+2212 MINUS SIGN rather than a hyphen because the two
// signs sit side by side in one column. So the remaining difference is the DASH
// CHARACTER and the `+`, deliberately — do not "unify" it into `formatCurrency`
// on the strength of the negatives now looking alike.
function formatSignedMoney(amount, fmt = formatCurrency) {
  if (amount < 0) return `−${fmt(Math.abs(amount))}`;
  if (amount > 0) return `+${fmt(amount)}`;
  return fmt(0);
}

export default HireAdjustmentDecisionDialog;
